import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const FLASH_CLASS = { up: "text-[#00ff41]", down: "text-[#ff3333]" };

const fmtNum = (n) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtPrice = (n) =>
  n >= 1
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `$${n.toFixed(6)}`;

function CoinCard({ c, flash }) {
  const change = c.change_24h ?? 0;
  const up = change >= 0;
  const spark = (c.sparkline || []).map((p, i) => ({ i, p }));
  return (
    <div
      className="border border-[#3a3a3a] bg-[#121212] p-5 hover:bg-[#1a1a1a] transition-colors"
      data-testid={`coin-card-${c.symbol.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {c.image ? (
            <img src={c.image} alt={c.symbol} className="w-7 h-7" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <span className="w-7 h-7 flex items-center justify-center bg-[#cfff04] text-[#050505] font-display font-bold text-xs">
              {c.symbol.slice(0, 1)}
            </span>
          )}
          <div>
            <div className="font-display text-base font-bold">{c.symbol}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
              {c.name}
            </div>
          </div>
        </div>
        <div
          className={`flex items-center gap-1 font-mono text-xs ${
            up ? "text-[#00ff41]" : "text-[#ff3333]"
          }`}
        >
          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {change.toFixed(2)}%
        </div>
      </div>
      <div
        className={`font-mono text-2xl font-bold tracking-tight transition-colors duration-500 ${
          FLASH_CLASS[flash] || ""
        }`}
      >
        {fmtPrice(c.price ?? 0)}
      </div>
      <div className="h-12 mt-3 -mx-2">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={{ width: 320, height: 240 }}>
          <AreaChart data={spark}>
            <defs>
              <linearGradient id={`g-${c.id}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={up ? "#00ff41" : "#ff3333"} stopOpacity={0.6} />
                <stop offset="100%" stopColor={up ? "#00ff41" : "#ff3333"} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="p"
              stroke={up ? "#00ff41" : "#ff3333"}
              fill={`url(#g-${c.id})`}
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 pt-3 border-t border-[#333] flex items-center justify-between font-mono text-[11px] uppercase tracking-wider">
        <span className="text-[#d0d0d0]">24h Vol</span>
        <span className="text-[#e2e2e2]">{fmtNum(c.volume_24h)}</span>
      </div>
    </div>
  );
}

export default function LiveMarket() {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [flashes, setFlashes] = useState({}); // symbol -> 'up'|'down'
  const [streaming, setStreaming] = useState(false);

  // Initial REST load (gives us sparklines + names + images) — retries transient failures
  useEffect(() => {
    let alive = true;
    let attempt = 0;
    const load = async () => {
      if (!alive) return;
      try {
        const { data } = await api.get("/market/top");
        if (alive) {
          setCoins(data.coins);
          setErr(null);
          setLoading(false);
        }
      } catch (e) {
        if (!alive) return;
        attempt += 1;
        if (attempt < 4) {
          setTimeout(load, attempt * 2000); // 2s, 4s, 6s backoff
        } else {
          setErr("Live feed temporarily unavailable — retrying…");
          setLoading(false);
          setTimeout(load, 15000); // keep self-healing in the background
        }
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  // Live SSE updates from Kraken WSS via backend
  useEffect(() => {
    const url = `${BACKEND_URL}/api/market/stream`;
    const es = new EventSource(url);
    es.onopen = () => setStreaming(true);
    es.onerror = () => setStreaming(false);

    const applyUpdate = (u) => {
      setCoins((prev) => {
        const idx = prev.findIndex((c) => c.symbol === u.symbol);
        if (idx === -1) {
          // REST load failed/missing — seed the row from the live stream instead
          setErr(null);
          setLoading(false);
          return [...prev, { id: u.symbol, name: u.symbol, image: null, sparkline: [], ...u }];
        }
        const old = prev[idx];
        let dir = null;
        if (typeof old.price === "number" && u.price !== old.price) {
          dir = u.price > old.price ? "up" : "down";
        }
        if (dir) {
          setFlashes((f) => ({ ...f, [u.symbol]: dir }));
          setTimeout(
            () =>
              setFlashes((f) => {
                const n = { ...f };
                delete n[u.symbol];
                return n;
              }),
            600
          );
        }
        const next = [...prev];
        next[idx] = {
          ...old,
          price: u.price,
          change_24h: u.change_24h,
          volume_24h: u.volume_24h,
        };
        return next;
      });
    };

    es.addEventListener("snapshot", (e) => {
      try {
        const list = JSON.parse(e.data);
        list.forEach(applyUpdate);
      } catch {
        // Intentional: malformed SSE frames are non-fatal; we get many per second.
        // Toasting or logging here would spam users and the console.
      }
    });
    es.onmessage = (e) => {
      try {
        applyUpdate(JSON.parse(e.data));
      } catch {
        // Intentional: see snapshot handler above.
      }
    };

    return () => es.close();
  }, []);

  return (
    <section id="market" className="relative border-t border-[#3a3a3a]" data-testid="section-market">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3 flex items-center gap-3">
              <span>01 // Live Market</span>
              <span className="flex items-center gap-1.5" data-testid="stream-status">
                <span
                  className={`w-1.5 h-1.5 ${
                    streaming ? "bg-[#00ff41] live-dot" : "bg-[#555]"
                  }`}
                />
                <span className={streaming ? "text-[#00ff41]" : "text-[#d0d0d0]"}>
                  {streaming ? "WSS LIVE" : "REST"}
                </span>
              </span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter">
              Real prices.
              <br />
              Real benchmarks.
            </h2>
          </div>
          <p className="max-w-md text-[#e2e2e2] text-sm">
            Live ticks straight from Kraken's WebSocket feed (snapshot via REST, sub-second updates via WSS).
            Compare SPURR against what your community already trades.
          </p>
        </div>

        {err && (
          <div className="border border-[#ff3333]/40 bg-[#1a0808] p-4 mb-6 font-mono text-xs text-[#ff3333]" data-testid="market-error">
            {err}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`market-skeleton-${i}`} className="border border-[#3a3a3a] bg-[#121212] p-5 h-44 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="market-grid">
            {coins.map((c) => (
              <CoinCard key={c.id} c={c} flash={flashes[c.symbol]} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
